import { ScheduledTasksPanel } from '../components/scheduled-tasks-panel.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApplicationDetailDto,
  ApplicationMaintenanceDto,
  DeploymentDetailDto,
  DeploymentDto,
  RuntimeLogsDto,
  UpdateApplicationInput,
} from '@cpm/shared';
import {
  AppWindow,
  ArrowLeft,
  ExternalLink,
  Link2,
  Pencil,
  Play,
  RefreshCw,
  RotateCw,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { Modal } from '../components/Modal.js';
import {
  EnvironmentPanel,
  FeatureNotice,
  StoragePanel,
} from '../components/resource-tabs.js';
import { ResourceMetricsPanel } from '../components/metrics-panels.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  Tabs,
  Textarea,
  statusTone,
} from '../components/ui.js';

const DEPLOYMENT_ACTIVE = ['queued', 'in_progress'];

/**
 * fqdn values come from Coolify, not from panel-validated input — only
 * render them as links when they are plain http(s) URLs.
 */
function safeHref(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

// ── Overview ─────────────────────────────────────────────────────────

function EditConfigForm({
  detail,
  onDone,
}: {
  detail: ApplicationDetailDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const live = detail.live;
  const [form, setForm] = useState({
    name: detail.resource.name,
    domainsText: live?.fqdn?.split(',').join('\n') ?? '',
    installCommand: live?.installCommand ?? '',
    buildCommand: live?.buildCommand ?? '',
    startCommand: live?.startCommand ?? '',
    baseDirectory: live?.baseDirectory ?? '',
    publishDirectory: live?.publishDirectory ?? '',
    portsExposes: live?.portsExposes ?? '',
    healthCheckEnabled: live?.healthCheck.enabled ?? false,
    healthCheckPath: live?.healthCheck.path ?? '/',
    healthCheckPort: live?.healthCheck.port ?? '',
  });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const update = useMutation({
    mutationFn: (input: UpdateApplicationInput) =>
      api.patch(`/api/applications/${detail.resource.id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['applications', detail.resource.id] });
      onDone();
    },
  });

  const domains = form.domainsText
    .split('\n')
    .map((d) => d.trim())
    .filter(Boolean);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({
          name: form.name,
          domains: detail.features.custom_domains && domains.length ? domains : undefined,
          installCommand: detail.features.application_runtime_configuration
            ? form.installCommand || undefined
            : undefined,
          buildCommand: detail.features.application_runtime_configuration
            ? form.buildCommand || undefined
            : undefined,
          startCommand: detail.features.application_runtime_configuration
            ? form.startCommand || undefined
            : undefined,
          baseDirectory: detail.features.application_runtime_configuration
            ? form.baseDirectory || undefined
            : undefined,
          publishDirectory: detail.features.application_runtime_configuration
            ? form.publishDirectory || undefined
            : undefined,
          portsExposes: detail.features.application_runtime_configuration
            ? form.portsExposes || undefined
            : undefined,
          healthCheck: detail.features.application_health_checks
            ? {
                enabled: form.healthCheckEnabled,
                path: form.healthCheckPath || undefined,
                port: form.healthCheckPort || undefined,
              }
            : undefined,
        });
      }}
    >
      <div>
        <Label htmlFor="cfg-name">Name</Label>
        <Input id="cfg-name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
      </div>
      <div>
        <Label htmlFor="cfg-domains">Domains (one URL per line)</Label>
        <Textarea
          id="cfg-domains"
          rows={2}
          disabled={!detail.features.custom_domains}
          value={form.domainsText}
          onChange={(e) => set({ domainsText: e.target.value })}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor="cfg-install">Install command</Label>
          <Input
            id="cfg-install"
            disabled={!detail.features.application_runtime_configuration}
            value={form.installCommand}
            onChange={(e) => set({ installCommand: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="cfg-build">Build command</Label>
          <Input
            id="cfg-build"
            disabled={!detail.features.application_runtime_configuration}
            value={form.buildCommand}
            onChange={(e) => set({ buildCommand: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="cfg-start">Start command</Label>
          <Input
            id="cfg-start"
            disabled={!detail.features.application_runtime_configuration}
            value={form.startCommand}
            onChange={(e) => set({ startCommand: e.target.value })}
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor="cfg-base">Base directory</Label>
          <Input
            id="cfg-base"
            disabled={!detail.features.application_runtime_configuration}
            value={form.baseDirectory}
            onChange={(e) => set({ baseDirectory: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="cfg-publish">Publish directory</Label>
          <Input
            id="cfg-publish"
            disabled={!detail.features.application_runtime_configuration}
            value={form.publishDirectory}
            onChange={(e) => set({ publishDirectory: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="cfg-ports">Exposed ports</Label>
          <Input
            id="cfg-ports"
            disabled={!detail.features.application_runtime_configuration}
            value={form.portsExposes}
            onChange={(e) => set({ portsExposes: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
            checked={form.healthCheckEnabled}
            disabled={!detail.features.application_health_checks}
            onChange={(e) => set({ healthCheckEnabled: e.target.checked })}
          />
          Health check enabled
        </label>
        {form.healthCheckEnabled && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cfg-hc-path">Path</Label>
              <Input
                id="cfg-hc-path"
                value={form.healthCheckPath}
                onChange={(e) => set({ healthCheckPath: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="cfg-hc-port">Port</Label>
              <Input
                id="cfg-hc-port"
                value={form.healthCheckPort}
                onChange={(e) => set({ healthCheckPort: e.target.value })}
              />
            </div>
          </div>
        )}
      </div>
      {update.error != null && <ErrorNote error={update.error} />}
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Changes apply on the next deployment.
      </p>
      <div className="flex justify-end">
        <Button type="submit" loading={update.isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

function OverviewTab({ detail }: { detail: ApplicationDetailDto }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [linkUuid, setLinkUuid] = useState('');
  const id = detail.resource.id;
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['applications', id] });

  const act = useMutation({
    mutationFn: (action: 'start' | 'stop' | 'restart' | 'deploy') =>
      api.post<{ deploymentUuid: string | null }>(
        `/api/applications/${id}/${action}`,
        action === 'deploy' ? { force: false } : undefined,
      ),
    onSuccess: invalidate,
  });
  const link = useMutation({
    mutationFn: () => api.post(`/api/applications/${id}/link`, { coolifyUuid: linkUuid }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/applications/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      void navigate(`/subscriptions/${detail.resource.subscriptionId}`);
    },
  });

  const canDeploy = detail.features.deployments && detail.resource.coolifyUuid !== null;
  const live = detail.live;

  return (
    <div className="space-y-4">
      {(act.error ?? remove.error) != null && <ErrorNote error={act.error ?? remove.error} />}

      {!detail.resource.coolifyUuid && (
        <Card>
          <CardHeader
            title="Not linked to Coolify"
            description="The panel reserved this application but has no Coolify UUID for it — paste the UUID from Coolify to repair the link."
          />
          <div className="flex flex-wrap items-center gap-2 px-5 py-4">
            <Input
              className="max-w-sm"
              placeholder="Coolify application UUID"
              value={linkUuid}
              onChange={(e) => setLinkUuid(e.target.value)}
            />
            <Button loading={link.isPending} disabled={!linkUuid.trim()} onClick={() => link.mutate()}>
              <Link2 className="size-4" /> Link
            </Button>
            {link.error != null && <ErrorNote error={link.error} />}
          </div>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader title="Status" />
          <div className="space-y-3 px-5 py-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-500 dark:text-slate-400">State</span>
              <Badge tone={statusTone(detail.resource.status)}>{detail.resource.status}</Badge>
            </div>
            {live?.fqdn && (
              <div>
                <p className="text-slate-500 dark:text-slate-400">Domain</p>
                {live.fqdn.split(',').map((domain) => {
                  const href = safeHref(domain.trim());
                  return href ? (
                    <a
                      key={domain}
                      href={href}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="flex items-center gap-1 break-all font-medium text-brand-600 hover:underline dark:text-brand-400"
                    >
                      {domain} <ExternalLink className="size-3.5 shrink-0" />
                    </a>
                  ) : (
                    <span key={domain} className="break-all font-medium">
                      {domain}
                    </span>
                  );
                })}
              </div>
            )}
            {detail.liveError && (
              <p className="text-xs text-amber-600 dark:text-amber-400">{detail.liveError}</p>
            )}
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
              <Button
                disabled={!canDeploy}
                loading={act.isPending}
                onClick={() => act.mutate('deploy')}
                title={detail.features.deployments ? undefined : 'Plan does not include deployments'}
              >
                <RotateCw className="size-4" /> Deploy
              </Button>
              <Button variant="secondary" disabled={!canDeploy} onClick={() => act.mutate('restart')}>
                <RefreshCw className="size-4" /> Restart
              </Button>
              <Button variant="secondary" disabled={!canDeploy} onClick={() => act.mutate('start')}>
                <Play className="size-4" /> Start
              </Button>
              <Button variant="secondary" disabled={!canDeploy} onClick={() => act.mutate('stop')}>
                <Square className="size-4" /> Stop
              </Button>
            </div>
            {!detail.features.deployments && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Lifecycle actions are disabled — the plan does not include deployments.
              </p>
            )}
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Configuration"
            actions={
              detail.resource.coolifyUuid && (
                <Button variant="secondary" onClick={() => setEditOpen(true)}>
                  <Pencil className="size-4" /> Edit
                </Button>
              )
            }
          />
          <dl className="grid gap-x-6 gap-y-1.5 px-5 py-4 text-sm sm:grid-cols-2">
            {(
              [
                ['Build pack', live?.buildPack],
                ['Repository', live?.gitRepository],
                ['Branch', live?.gitBranch],
                ['Image', live?.dockerRegistryImageName ? `${live.dockerRegistryImageName}:${live.dockerRegistryImageTag ?? 'latest'}` : null],
                ['Install command', live?.installCommand],
                ['Build command', live?.buildCommand],
                ['Start command', live?.startCommand],
                ['Base directory', live?.baseDirectory],
                ['Publish directory', live?.publishDirectory],
                ['Exposed ports', live?.portsExposes],
                ['Memory limit', live?.limitsMemory === '0' ? 'Unlimited' : live?.limitsMemory],
                ['CPU limit', live?.limitsCpus === '0' ? 'Unlimited' : live?.limitsCpus],
                [
                  'Health check',
                  live
                    ? live.healthCheck.enabled
                      ? `${live.healthCheck.path ?? '/'}${live.healthCheck.port ? ` :${live.healthCheck.port}` : ''}`
                      : 'disabled'
                    : null,
                ],
                ['Coolify UUID', detail.resource.coolifyUuid],
              ] as Array<[string, string | null | undefined]>
            )
              .filter(([, value]) => value != null && value !== '')
              .map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0 dark:border-slate-800">
                  <dt className="shrink-0 text-slate-500 dark:text-slate-400">{label}</dt>
                  <dd className="break-all text-right font-medium">{value}</dd>
                </div>
              ))}
          </dl>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Danger zone"
          description="Deletes the application in Coolify (including volumes), then releases the plan quota. Admins only."
        />
        <div className="px-5 py-4">
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Delete this application? It is torn down in Coolify (containers and volumes) and removed from the ledger.',
                )
              ) {
                remove.mutate();
              }
            }}
          >
            <Trash2 className="size-4" /> Delete application
          </Button>
        </div>
      </Card>

      <Modal title="Edit configuration" open={editOpen} onClose={() => setEditOpen(false)} wide>
        <EditConfigForm detail={detail} onDone={() => setEditOpen(false)} />
      </Modal>
    </div>
  );
}

// ── Deployments ──────────────────────────────────────────────────────

function DeploymentLogsPanel({ appId, deploymentUuid }: { appId: string; deploymentUuid: string }) {
  const queryClient = useQueryClient();
  const openedAt = useRef(Date.now());

  const deployment = useQuery({
    queryKey: ['applications', appId, 'deployments', deploymentUuid],
    queryFn: () =>
      api.get<DeploymentDetailDto>(`/api/applications/${appId}/deployments/${deploymentUuid}`),
    // Poll only while the deployment is running, backing off over time to
    // respect Coolify's 200 req/min budget (ADR-0006).
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status || !DEPLOYMENT_ACTIVE.includes(status)) return false;
      const elapsed = Date.now() - openedAt.current;
      return elapsed < 30_000 ? 3_000 : elapsed < 120_000 ? 8_000 : 15_000;
    },
  });

  const cancel = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>(
        `/api/applications/${appId}/deployments/${deploymentUuid}/cancel`,
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: ['applications', appId, 'deployments'],
      }),
  });

  if (deployment.isPending) return <Spinner label="Loading build logs…" />;
  if (deployment.error) return <ErrorNote error={deployment.error} />;
  const data = deployment.data;
  const active = DEPLOYMENT_ACTIVE.includes(data.status);
  const visibleLogs = data.logs.filter((line) => !line.hidden);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm">
          <Badge tone={statusTone(data.status)}>{data.status}</Badge>
          {data.commit && (
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">
              {data.commit.slice(0, 8)}
            </code>
          )}
          <span className="text-slate-500 dark:text-slate-400">{data.commitMessage}</span>
        </div>
        {active && (
          <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
            <X className="size-4" /> Cancel deployment
          </Button>
        )}
      </div>
      {cancel.error != null && <ErrorNote error={cancel.error} />}
      <pre className="max-h-96 overflow-auto rounded-lg bg-slate-950 p-4 text-xs leading-relaxed text-slate-100">
        {visibleLogs.length === 0
          ? 'No log output yet.'
          : visibleLogs.map((line, index) => (
              <div key={index} className={line.type === 'stderr' ? 'text-red-400' : undefined}>
                {line.output}
              </div>
            ))}
      </pre>
      {active && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Refreshing automatically while the deployment runs…
        </p>
      )}
    </div>
  );
}

function DeploymentsTab({ detail }: { detail: ApplicationDetailDto }) {
  const appId = detail.resource.id;
  const [selected, setSelected] = useState<string | null>(null);

  const deployments = useQuery({
    queryKey: ['applications', appId, 'deployments'],
    queryFn: () => api.get<DeploymentDto[]>(`/api/applications/${appId}/deployments?take=20`),
  });

  if (!detail.features.deployments) return <FeatureNotice feature="deployments" />;
  if (deployments.isPending) return <Spinner />;
  if (deployments.error) return <ErrorNote error={deployments.error} />;

  return (
    <Card>
      <CardHeader
        title="Deployment history"
        description="The 20 most recent deployments, newest first"
        actions={
          <Button variant="secondary" onClick={() => void deployments.refetch()}>
            <RefreshCw className="size-4" /> Refresh
          </Button>
        }
      />
      {deployments.data.length === 0 ? (
        <EmptyState title="No deployments yet" description="Trigger the first deployment from the Overview tab." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Commit</th>
                <th className="px-5 py-3 font-medium">Started</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {deployments.data.map((deployment) => (
                <tr key={deployment.deploymentUuid}>
                  <td className="px-5 py-3">
                    <Badge tone={statusTone(deployment.status)}>{deployment.status}</Badge>
                    {deployment.forceRebuild && (
                      <span className="ml-2 text-xs text-slate-400">forced</span>
                    )}
                  </td>
                  <td className="max-w-md px-5 py-3">
                    {deployment.commit && (
                      <code className="mr-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">
                        {deployment.commit.slice(0, 8)}
                      </code>
                    )}
                    <span className="text-slate-600 dark:text-slate-300">
                      {deployment.commitMessage ?? '—'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-slate-500 dark:text-slate-400">
                    {deployment.createdAt ? new Date(deployment.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setSelected(
                          selected === deployment.deploymentUuid ? null : deployment.deploymentUuid,
                        )
                      }
                    >
                      {selected === deployment.deploymentUuid ? 'Hide logs' : 'Build logs'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selected && (
        <div className="border-t border-slate-200 px-5 py-4 dark:border-slate-800">
          <DeploymentLogsPanel appId={appId} deploymentUuid={selected} />
        </div>
      )}
    </Card>
  );
}

// ── Runtime logs ─────────────────────────────────────────────────────

function LogsTab({ detail }: { detail: ApplicationDetailDto }) {
  const appId = detail.resource.id;
  const [lines, setLines] = useState(100);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const logs = useQuery({
    queryKey: ['applications', appId, 'logs', lines],
    queryFn: () => api.get<RuntimeLogsDto>(`/api/applications/${appId}/logs?lines=${lines}`),
    enabled: detail.features.logs && detail.resource.coolifyUuid !== null,
    // Manual refresh by default; the auto toggle polls at a gentle 10s.
    refetchInterval: autoRefresh ? 10_000 : false,
  });

  if (!detail.features.logs) return <FeatureNotice feature="logs" />;

  return (
    <Card>
      <CardHeader
        title="Runtime logs"
        description="Tail of the container output — Coolify has no streaming API, so logs load on demand"
        actions={
          <>
            <Select
              className="w-auto"
              value={lines}
              onChange={(e) => setLines(Number(e.target.value))}
              aria-label="Number of log lines"
            >
              {[100, 500, 1000, 5000].map((n) => (
                <option key={n} value={n}>
                  {n} lines
                </option>
              ))}
            </Select>
            <label className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto (10s)
            </label>
            <Button variant="secondary" loading={logs.isFetching} onClick={() => void logs.refetch()}>
              <RefreshCw className="size-4" /> Refresh
            </Button>
          </>
        }
      />
      <div className="px-5 py-4">
        {logs.isPending ? (
          <Spinner label="Loading logs…" />
        ) : logs.error ? (
          <ErrorNote error={logs.error} />
        ) : (
          <pre className="max-h-[32rem] overflow-auto rounded-lg bg-slate-950 p-4 text-xs leading-relaxed text-slate-100">
            {logs.data.logs || 'No output.'}
          </pre>
        )}
      </div>
    </Card>
  );
}

// ── Scheduled tasks + maintenance ───────────────────────────────────


function MaintenanceTab({ detail }: { detail: ApplicationDetailDto }) {
  const appId = detail.resource.id;
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const maintenance = useQuery({
    queryKey: ['applications', appId, 'maintenance'],
    queryFn: () =>
      api.get<ApplicationMaintenanceDto>(
        `/api/applications/${appId}/maintenance`,
      ),
    enabled:
      detail.features.application_maintenance === true
      && detail.resource.coolifyUuid !== null,
  });
  const setMaintenance = useMutation({
    mutationFn: (enabled: boolean) =>
      api.put<ApplicationMaintenanceDto>(
        `/api/applications/${appId}/maintenance`,
        { enabled, reason: enabled ? reason || null : null },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['applications', appId, 'maintenance'],
      });
      void queryClient.invalidateQueries({ queryKey: ['applications', appId] });
    },
  });

  if (!detail.features.application_maintenance) {
    return <FeatureNotice feature="application_maintenance" />;
  }
  if (maintenance.isPending) return <Spinner />;
  if (maintenance.error) return <ErrorNote error={maintenance.error} />;

  return (
    <Card>
      <CardHeader
        title="Maintenance"
        description="Portable replacement: CPM stops the application and records the reason. This does not recreate Plesk's maintenance page."
      />
      <div className="space-y-4 px-5 py-4">
        <div className="flex items-center gap-2">
          <Badge tone={maintenance.data.enabled ? 'yellow' : 'green'}>
            {maintenance.data.enabled ? 'application stopped' : 'application serving'}
          </Badge>
          {maintenance.data.reason && (
            <span className="text-sm text-slate-600 dark:text-slate-300">
              {maintenance.data.reason}
            </span>
          )}
        </div>
        {!maintenance.data.enabled && (
          <div className="max-w-xl">
            <Label htmlFor="maintenance-reason">Reason</Label>
            <Input
              id="maintenance-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Planned migration or maintenance"
            />
          </div>
        )}
        {setMaintenance.error != null && <ErrorNote error={setMaintenance.error} />}
        <Button
          variant={maintenance.data.enabled ? 'secondary' : 'danger'}
          loading={setMaintenance.isPending}
          onClick={() => setMaintenance.mutate(!maintenance.data.enabled)}
        >
          {maintenance.data.enabled ? 'Resume application' : 'Stop for maintenance'}
        </Button>
      </div>
    </Card>
  );
}

// ── Page ─────────────────────────────────────────────────────────────

export function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState('overview');

  const detail = useQuery({
    queryKey: ['applications', id],
    queryFn: () => api.get<ApplicationDetailDto>(`/api/applications/${id}`),
    enabled: Boolean(id),
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const data = detail.data;
  const linked = data.resource.coolifyUuid !== null;

  return (
    <div>
      <Link
        to={`/subscriptions/${data.subscription.id}`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" /> {data.subscription.name}
      </Link>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <AppWindow className="size-6 text-slate-400" />
            {data.resource.name}
            <Badge tone={statusTone(data.resource.status)}>{data.resource.status}</Badge>
          </span>
        }
        description={
          <>
            Application of{' '}
            <Link
              to={`/clients/${data.subscription.clientId}`}
              className="text-brand-600 hover:underline dark:text-brand-400"
            >
              {data.subscription.clientName}
            </Link>{' '}
            · subscription{' '}
            <Link
              to={`/subscriptions/${data.subscription.id}`}
              className="text-brand-600 hover:underline dark:text-brand-400"
            >
              {data.subscription.name}
            </Link>
          </>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'metrics', label: 'Metrics', disabled: !linked },
          { key: 'deployments', label: 'Deployments', disabled: !linked },
          { key: 'logs', label: 'Logs', disabled: !linked },
          { key: 'scheduled-tasks', label: 'Scheduled tasks', disabled: !linked },
          { key: 'maintenance', label: 'Maintenance', disabled: !linked },
          { key: 'environment', label: 'Environment', disabled: !linked },
          { key: 'storage', label: 'Storage', disabled: !linked },
        ]}
      />

      {tab === 'overview' && <OverviewTab detail={data} />}
      {tab === 'metrics' && <ResourceMetricsPanel resourceId={data.resource.id} />}
      {tab === 'deployments' && <DeploymentsTab detail={data} />}
      {tab === 'logs' && <LogsTab detail={data} />}
      {tab === 'scheduled-tasks' && <ScheduledTasksPanel appId={data.resource.id} enabled={data.features.application_scheduled_tasks === true && data.resource.coolifyUuid !== null} />}
      {tab === 'maintenance' && <MaintenanceTab detail={data} />}
      {tab === 'environment' && (
        <EnvironmentPanel
          apiBase={`/api/applications/${data.resource.id}`}
          queryKey={['applications', data.resource.id, 'envs']}
          enabled={linked}
          featureEnabled={data.features.environment_variables ?? false}
        />
      )}
      {tab === 'storage' && (
        <StoragePanel
          apiBase={`/api/applications/${data.resource.id}`}
          queryKey={['applications', data.resource.id, 'storages']}
          enabled={linked}
        />
      )}
    </div>
  );
}
